import React, { Fragment, forwardRef, useContext, useRef, useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Grid, Typography } from '@mui/material';

import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { BaseSection } from 'shared/sections/BaseSection/BaseSection';
import { SectionTitle } from 'shared/sections/SectionTitle/SectionTitle';
import { SectionCTA } from 'shared/components/SectionCTA/SectionCTA';
import mpLogo from 'shared/img/logos/mpLogo.webp';
import catLogo from 'shared/img/logos/cat.png';
import ikeaLogo from 'shared/img/logos/ikeaLogo.png';
import samsungLogo from 'shared/img/logos/samsungLogo.svg';
import { MenuItemElement } from 'contexts/LayoutContext/LayoutContext.types';

const animationStyles = `
  @keyframes strikethrough {
    0% { width: 0; }
    100% { width: 100%; }
  }
  @keyframes blink {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 0; }
  }
  @keyframes twinkle {
    0% { transform: translateX(-100%) skewX(-15deg); }
    100% { transform: translateX(300%) skewX(-15deg); }
  }
  @keyframes xSlam1 {
    0% { transform: rotate(13deg) scale(6); opacity: 0; filter: blur(4px); }
    50% { opacity: 1; filter: blur(0px); }
    75% { transform: rotate(13deg) scale(0.95); }
    100% { transform: rotate(13deg) scale(1); opacity: 1; filter: blur(0px); }
  }
  @keyframes xSlam2 {
    0% { transform: rotate(-19deg) scale(6); opacity: 0; filter: blur(4px); }
    50% { opacity: 1; filter: blur(0px); }
    75% { transform: rotate(-19deg) scale(0.95); }
    100% { transform: rotate(-19deg) scale(1); opacity: 1; filter: blur(0px); }
  }
  @keyframes circleSlam {
    0% { transform: scale(5) rotate(-8deg); opacity: 0; filter: blur(3px); }
    50% { opacity: 1; filter: blur(0px); }
    75% { transform: scale(0.93) rotate(-3deg); }
    100% { transform: scale(1) rotate(-2deg); opacity: 1; filter: blur(0px); }
  }
  @keyframes stampIn {
    0% { transform: scale(20); opacity: 0; filter: blur(8px); }
    50% { opacity: 1; filter: blur(0px); }
    75% { transform: scale(0.9); }
    100% { transform: scale(1); opacity: 1; filter: blur(0px); }
  }
  @keyframes drawLine {
    0% { stroke-dashoffset: 1000; opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 1; }
  }
  @keyframes sparkTravel {
    0% { stroke-dashoffset: 1000; opacity: 1; }
    90% { opacity: 1; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }
  @keyframes cometTrail {
    0% { stroke-dashoffset: 1000; opacity: 0.7; }
    90% { opacity: 0.5; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }
  @keyframes sparkleTrail {
    0% { stroke-dashoffset: 1000; opacity: 0.8; }
    90% { opacity: 0.6; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }
  @keyframes fillIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes shadowIn {
    0% { opacity: 0; }
    100% { opacity: 0.47; }
  }
`;

// Each letter has two paths:
// - d: continuous path (smooth spark travel, includes phantom connecting edges)
// - dClean: split path (visible outline only, no phantom edges)
const letterPaths = [
  { // C: continuous loop including phantom edge across opening
    d: "M0,55.95 L48.16,55.95 L57.93,41 L23.76,41 L30.32,29.93 L51.68,29.93 L43.23,14.98 L22.76,14.98 L0,55.95",
    dClean: "M0,55.95 L22.76,14.98 L43.23,14.98 L51.68,29.93 L30.32,29.93 L23.76,41 L57.93,41 M0,55.95 L48.16,55.95",
    dur: 0.5, delay: 0,
  },
  { // V: continuous through phantom edge, smooth inner→outer
    d: "M89,14.98 L66.36,55.93 L57.93,41 L48.16,55.95 L65.86,87.73 L97.82,29.94",
    dClean: "M89,14.98 L66.36,55.93 L57.93,41 M48.16,55.95 L65.86,87.73 L97.82,29.94",
    dur: 0.4, delay: 0.5,
  },
  { // T: fully continuous, all real edges
    d: "M97.82,29.94 L105.18,29.94 L90.72,55.95 L106.17,55.95 L120.97,29.94 L133.28,29.94 L141.73,14.99 L129.48,14.98 L113.49,14.98 L106.08,14.98 L114.37,0 L34.92,0.03 L43.23,14.98 L89,14.98",
    dClean: "M97.82,29.94 L105.18,29.94 L90.72,55.95 L106.17,55.95 L120.97,29.94 L133.28,29.94 L141.73,14.99 L129.48,14.98 L113.49,14.98 L106.08,14.98 L114.37,0 L34.92,0.03 L43.23,14.98 L89,14.98",
    dur: 0.6, delay: 0.9,
  },
];
const totalDrawTime = 1.5; // 0.9 + 0.6

const mainPoints = "141.73 14.99 129.48 14.99 129.48 14.98 113.49 14.98 113.48 14.98 106.08 14.98 114.37 0 34.92 0.03 43.23 14.98 89 14.98 66.36 55.93 57.93 41 23.76 41 30.32 29.93 51.68 29.93 43.23 14.98 22.76 14.98 0 55.95 48.16 55.95 65.86 87.73 97.82 29.94 105.18 29.94 90.72 55.95 106.17 55.95 120.97 29.94 133.28 29.94 141.73 14.99";
const shadow1Points = "15.92 41.05 15.23 55.95 48.16 55.95 40.7 41 15.92 41.05";
const shadow2Points = "130.06 15.03 130.75 29.93 97.82 29.93 106.08 14.98 130.06 15.03";

const CVTLogo = ({ height, style }: { height: number; style?: React.CSSProperties }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-4 -4 150 96"
    style={{ height, width: 'auto', overflow: 'visible', ...style }}
  >
    <defs>
      <linearGradient id="cvt-grad-main" x1="-40" y1="43.86" x2="180" y2="43.86" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#6365ff"/>
        <stop offset="0.32" stopColor="#7039ec"/>
        <stop offset="0.6" stopColor="#791adf"/>
        <stop offset="0.84" stopColor="#7e07d7"/>
        <stop offset="1" stopColor="#8000d4"/>
        <animateTransform attributeName="gradientTransform" type="rotate" from="0 70.87 43.86" to="360 70.87 43.86" dur="2s" repeatCount="indefinite"/>
      </linearGradient>
      <radialGradient id="cvt-grad-glow" cx="408.82" cy="-135.28" r="66.47" gradientTransform="translate(-269.25 110.1) scale(0.83)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#15fffb"/>
        <stop offset="0.11" stopColor="#15fffb" stopOpacity="0.87"/>
        <stop offset="0.44" stopColor="#15fffb" stopOpacity="0.5"/>
        <stop offset="0.71" stopColor="#15fffb" stopOpacity="0.23"/>
        <stop offset="0.9" stopColor="#15fffb" stopOpacity="0.06"/>
        <stop offset="1" stopColor="#15fffb" stopOpacity="0"/>
        <animate attributeName="cx" values="408.82;460;370;450;380;408.82" dur="12s" repeatCount="indefinite"/>
        <animate attributeName="cy" values="-135.28;-80;-170;-90;-160;-135.28" dur="16s" repeatCount="indefinite"/>
      </radialGradient>
      <linearGradient id="cvt-grad-shadow1" x1="-2326.33" y1="45.93" x2="-2285.78" y2="58.85" gradientTransform="matrix(-1, 0, 0, 1, -2283.08, -1)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#000"/>
        <stop offset="0.59" stopColor="#000" stopOpacity="0"/>
      </linearGradient>
      <linearGradient id="cvt-grad-shadow2" x1="534.94" y1="19.9" x2="575.48" y2="32.83" gradientTransform="translate(-432.2 -1)" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#000"/>
        <stop offset="0.59" stopColor="#000" stopOpacity="0"/>
      </linearGradient>
      <filter id="spark-glow" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="innerBlur"/>
        <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="midBlur"/>
        <feGaussianBlur in="SourceGraphic" stdDeviation="10" result="outerBlur"/>
        <feMerge>
          <feMergeNode in="outerBlur"/>
          <feMergeNode in="midBlur"/>
          <feMergeNode in="innerBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
      <filter id="trail-glow" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur"/>
        <feMerge>
          <feMergeNode in="blur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>

    {/* One continuous spark per letter: C → V → T */}
    {letterPaths.map(({ d, dClean, dur, delay }, i) => (
      <g key={i}>
        {/* Outline uses clean path (no phantom edges) */}
        <path
          d={dClean}
          fill="none"
          stroke="url(#cvt-grad-main)"
          strokeWidth="1.5"
          strokeLinejoin="miter"
          pathLength={1000}
          strokeDasharray="1000"
          strokeDashoffset="1000"
          style={{ opacity: 0, animation: `drawLine ${dur}s ${delay}s ease-in-out forwards` }}
        />
        {/* Long fading comet tail */}
        <path
          d={d}
          fill="none"
          stroke="#6365ff"
          strokeWidth="4"
          strokeLinecap="round"
          pathLength={1000}
          strokeDasharray="250 750"
          strokeDashoffset="1000"
          filter="url(#trail-glow)"
          style={{ opacity: 0, animation: `cometTrail ${dur}s ${delay}s ease-in-out forwards` }}
        />
        {/* Sparkle particles — dashed trail behind the head */}
        <path
          d={d}
          fill="none"
          stroke="#15fffb"
          strokeWidth="2"
          strokeLinecap="round"
          pathLength={1000}
          strokeDasharray="3 12 2 15 4 10 1 18 3 14 2 906"
          strokeDashoffset="1000"
          style={{ opacity: 0, animation: `sparkleTrail ${dur}s ${delay}s ease-in-out forwards` }}
        />
        {/* Outer cyan glow head */}
        <path
          d={d}
          fill="none"
          stroke="#15fffb"
          strokeWidth="8"
          strokeLinecap="round"
          pathLength={1000}
          strokeDasharray="25 975"
          strokeDashoffset="1000"
          filter="url(#spark-glow)"
          style={{ opacity: 0, animation: `sparkTravel ${dur}s ${delay}s ease-in-out forwards` }}
        />
        {/* Hot white core */}
        <path
          d={d}
          fill="none"
          stroke="#ffffff"
          strokeWidth="4"
          strokeLinecap="round"
          pathLength={1000}
          strokeDasharray="18 982"
          strokeDashoffset="1000"
          style={{ opacity: 0, animation: `sparkTravel ${dur}s ${delay}s ease-in-out forwards` }}
        />
      </g>
    ))}

    {/* Fill fades in after all outlines are drawn */}
    <g style={{ animation: `fillIn 0.5s ${totalDrawTime}s ease-out forwards`, opacity: 0 }}>
      <polygon fill="url(#cvt-grad-main)" points={mainPoints}/>
      <polygon fill="url(#cvt-grad-glow)" points={mainPoints}/>
    </g>

    {/* Shadows fade in last */}
    <polygon fill="url(#cvt-grad-shadow1)" points={shadow1Points} style={{ animation: `shadowIn 0.4s ${totalDrawTime + 0.3}s ease-out forwards`, opacity: 0 }}/>
    <polygon fill="url(#cvt-grad-shadow2)" points={shadow2Points} style={{ animation: `shadowIn 0.4s ${totalDrawTime + 0.3}s ease-out forwards`, opacity: 0 }}/>
  </svg>
);

export const HomeSection = forwardRef<MenuItemElement>((props, ref) => {
  const navigate = useNavigate();
  const { sectionId = 'home' } = useParams<{ sectionId: string }>();
  const firstSection = useRef(sectionId);
  const isMentourLandingPage = useMemo(() => {
    return firstSection.current === 'mentour';
  }, [])

  const { isMobile, useDarkTheme, whySectionRef } = useContext(LayoutContext);

  // Animation phases:
  // 0=hidden, 1=stamp C, 2=stamp V, 3=stamp T, 4=expand words + "with",
  // 5=show "Vibe Coding", 6=X slam + gray, 7=show "Value Coding",
  // 8=type sentence 1, 9=type sentence 2, 10=show CTA,
  // 11=logo 1, 12=logo 2, 13=logo 3
  const [phase, setPhase] = useState(0);
  const [typedChars1, setTypedChars1] = useState(0);
  const [typedChars2, setTypedChars2] = useState(0);

  const sentence1 = 'We deliver valuable outcomes to founders and businesses';
  const sentence2 = 'Combining Ownership & Accountability of Passionate Experienced Humans with the Speed & Power of AI';

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 500),
      setTimeout(() => setPhase(2), 950),
      setTimeout(() => setPhase(3), 1400),
      setTimeout(() => setPhase(4), 2200),
      setTimeout(() => setPhase(5), 3200),
      setTimeout(() => setPhase(6), 4000),
      setTimeout(() => setPhase(7), 4600),
      setTimeout(() => setPhase(8), 5400),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  // Typewriter for sentence 1
  useEffect(() => {
    if (phase < 8) return;
    const interval = setInterval(() => {
      setTypedChars1(prev => {
        if (prev >= sentence1.length) { clearInterval(interval); return prev; }
        return prev + 1;
      });
    }, 55);
    return () => clearInterval(interval);
  }, [phase >= 8]); // eslint-disable-line

  // Typewriter for sentence 2 (starts after sentence 1 finishes)
  useEffect(() => {
    if (typedChars1 < sentence1.length) return;
    setPhase(9);
    const interval = setInterval(() => {
      setTypedChars2(prev => {
        if (prev >= sentence2.length) {
          clearInterval(interval);
          // Trigger remaining phases
          setTimeout(() => setPhase(10), 300);
          setTimeout(() => setPhase(11), 800);
          setTimeout(() => setPhase(12), 1200);
          setTimeout(() => setPhase(13), 1600);
          setTimeout(() => window.dispatchEvent(new Event('homeAnimationComplete')), 2000);
          return prev;
        }
        return prev + 1;
      });
    }, 40);
    return () => clearInterval(interval);
  }, [typedChars1 >= sentence1.length]); // eslint-disable-line

  const skipAnimation = () => {
    setPhase(13);
    setTypedChars1(sentence1.length);
    setTypedChars2(sentence2.length);
    window.dispatchEvent(new Event('homeAnimationComplete'));
  };

  const wordTransition = 'max-width 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.6s ease';

  return (
    <BaseSection ref={ref} style={{ padding: 12 }}>
      <style>{animationStyles}</style>
      <CVTLogo
        height={isMobile ? 120 : 150}
        style={{ marginTop: isMobile ? 100 : (isMentourLandingPage ? 202 : 262), marginLeft: 12, marginBottom: 16 }}
      />
      <SectionTitle color="secondary" onEnterViewport={() => sectionId !== 'mentour' && navigate('/home')}>
        <span style={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'center', flexWrap: 'wrap', gap: phase >= 4 ? '0 0.25em' : '0 0.15em', textShadow: '0 0 12px rgba(0,0,0,0.5), 0 0 24px rgba(0,0,0,0.3)', padding: '4px 0', fontFamily: "'Montserrat', sans-serif", fontWeight: 600, fontSize: isMobile ? '8vw' : undefined, transform: phase >= 4 ? 'scale(1)' : 'scale(2)', transition: 'transform 0.8s cubic-bezier(0.22, 1, 0.36, 1), gap 0.8s ease' }}>
          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
            <span style={{
              display: 'inline-block',
              opacity: phase >= 1 ? 1 : 0,
              animation: phase >= 1 ? 'stampIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}>C</span>
            <span style={{
              display: 'inline-block',
              maxWidth: phase >= 4 ? '10em' : '0',
              opacity: phase >= 4 ? 1 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: wordTransition,
            }}>reating</span>
          </span>
          <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'baseline' }}>
            <span style={{
              position: 'absolute',
              top: isMobile ? 'calc(-0.6em + 3px)' : 'calc(-0.6em + 8px)',
              right: -4,
              fontSize: '0.4em',
              fontWeight: 'inherit',
              opacity: phase >= 4 ? 1 : 0,
              transition: 'opacity 0.5s ease',
              whiteSpace: 'nowrap',
            }}>with</span>
            <span style={{
              display: 'inline-block',
              opacity: phase >= 2 ? 1 : 0,
              animation: phase >= 2 ? 'stampIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}>V</span>
            <span style={{
              display: 'inline-block',
              maxWidth: phase >= 4 ? '10em' : '0',
              opacity: phase >= 4 ? 1 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: wordTransition,
            }}>alue</span>
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
            <span style={{
              display: 'inline-block',
              opacity: phase >= 3 ? 1 : 0,
              animation: phase >= 3 ? 'stampIn 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}>T</span>
            <span style={{
              display: 'inline-block',
              maxWidth: phase >= 4 ? '10em' : '0',
              opacity: phase >= 4 ? 1 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              transition: wordTransition,
            }}>ech</span>
          </span>
        </span>
      </SectionTitle>

      <Box sx={{
        mt: 1,
        opacity: phase >= 5 ? 1 : 0,
        transform: phase >= 5 ? 'translateY(0)' : 'translateY(12px)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="div" style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.4em', color: '#6365FF', textShadow: '0 0 8px rgba(0,0,0,0.25)', flexWrap: isMobile ? 'wrap' as const : 'nowrap' as const, justifyContent: 'center', fontSize: isMobile ? '6vw' : undefined }}>
          <span style={{
            position: 'relative',
            display: 'inline-block',
            color: phase >= 6 ? (useDarkTheme ? '#666' : '#aaa') : '#6365FF',
            transition: 'color 0.5s ease',
          }}>
            Vibe Coding
            {/* X slash 1 — slams in */}
            <span style={{
              position: 'absolute',
              left: '-2%',
              top: '50%',
              width: '104%',
              height: '2px',
              background: useDarkTheme ? '#666' : '#aaa',
              transformOrigin: 'center center',
              opacity: 0,
              pointerEvents: 'none',
              animation: phase >= 6 ? 'xSlam1 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}/>
            {/* X slash 2 — slams in with delay */}
            <span style={{
              position: 'absolute',
              left: '3%',
              top: '50%',
              width: '100%',
              height: '2px',
              background: useDarkTheme ? '#666' : '#aaa',
              transformOrigin: 'center center',
              opacity: 0,
              pointerEvents: 'none',
              animation: phase >= 6 ? 'xSlam2 0.35s 0.12s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
            }}/>
          </span>
          <span style={{ position: 'relative', display: 'inline' }}>
            <span style={{
              display: 'inline-block',
              maxWidth: phase >= 7 ? '10em' : '0',
              opacity: phase >= 7 ? 1 : 0,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              verticalAlign: 'top',
              transition: 'max-width 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease',
            }}>Value Coding</span>
            {/* Hand-drawn circle burned around Value Coding */}
            {(() => {
              const circlePath = "M45,22 C100,-10 200,-18 268,2 C310,14 318,42 290,62 C255,85 175,96 95,88 C30,82 -12,65 -5,42 C0,28 22,8 55,2";
              const circleDelay = 7.2;
              return (
                <svg
                  viewBox="-10 -10 320 100"
                  style={{
                    position: 'absolute',
                    left: '-22%',
                    top: '-45%',
                    width: '144%',
                    height: '190%',
                    pointerEvents: 'none',
                    overflow: 'visible',
                    transform: 'rotate(-2deg)',
                    zIndex: -1,
                  }}
                >
                  <defs>
                    <filter id="circle-spark-glow" x="-100%" y="-100%" width="300%" height="300%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="innerBlur"/>
                      <feGaussianBlur in="SourceGraphic" stdDeviation="5" result="midBlur"/>
                      <feMerge>
                        <feMergeNode in="midBlur"/>
                        <feMergeNode in="innerBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    <filter id="circle-trail-glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur"/>
                      <feMerge>
                        <feMergeNode in="blur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  {/* Outline */}
                  <path d={circlePath} fill="none" stroke="#8000D4" strokeWidth="2.5" strokeLinecap="round"
                    pathLength={1000} strokeDasharray="1000" strokeDashoffset="1000"
                    style={{ opacity: 0, animation: phase >= 8 ? `drawLine 0.6s ${circleDelay - 7.2}s ease-in-out forwards` : 'none' }}/>
                  {/* Comet trail */}
                  <path d={circlePath} fill="none" stroke="#8000D4" strokeWidth="4" strokeLinecap="round"
                    pathLength={1000} strokeDasharray="250 750" strokeDashoffset="1000" filter="url(#circle-trail-glow)"
                    style={{ opacity: 0, animation: phase >= 8 ? `cometTrail 0.6s ${circleDelay - 7.2}s ease-in-out forwards` : 'none' }}/>
                  {/* Spark glow */}
                  <path d={circlePath} fill="none" stroke="#15fffb" strokeWidth="5" strokeLinecap="round"
                    pathLength={1000} strokeDasharray="25 975" strokeDashoffset="1000" filter="url(#circle-spark-glow)"
                    style={{ opacity: 0, animation: phase >= 8 ? `sparkTravel 0.6s ${circleDelay - 7.2}s ease-in-out forwards` : 'none' }}/>
                  {/* Hot core */}
                  <path d={circlePath} fill="none" stroke="#ffffff" strokeWidth="2" strokeLinecap="round"
                    pathLength={1000} strokeDasharray="18 982" strokeDashoffset="1000"
                    style={{ opacity: 0, animation: phase >= 8 ? `sparkTravel 0.6s ${circleDelay - 7.2}s ease-in-out forwards` : 'none' }}/>
                </svg>
              );
            })()}
          </span>
        </Typography>
      </Box>

      <Box sx={{ minHeight: phase >= 8 ? 'auto' : 0 }}>
        <br/>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontFamily="Montserrat" px={isMobile ? 2 : 0}
          sx={{ visibility: phase >= 8 ? 'visible' : 'hidden', minHeight: phase >= 8 ? 'auto' : 0 }}
        >
          {sentence1.split('').map((char, i) => (
            <span key={i} style={{
              opacity: i < typedChars1 ? 1 : 0.15,
              transition: 'opacity 0.15s ease',
            }}>{char}</span>
          ))}
        </Typography>
        <Typography variant={isMobile ? 'h6' : 'h5'} px={isMobile ? 2 : 0}
          sx={{ visibility: phase >= 9 ? 'visible' : 'hidden', minHeight: phase >= 9 ? 'auto' : 0 }}
        >
          {sentence2.split('').map((char, i) => (
            <span key={i} style={{
              opacity: i < typedChars2 ? 1 : 0.15,
              transition: 'opacity 0.15s ease',
            }}>{char}</span>
          ))}
        </Typography>
        <Box sx={{
          opacity: phase >= 10 ? 1 : 0,
          transform: phase >= 10 ? 'translateY(0)' : 'translateY(15px)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
        }}>
          <SectionCTA
            nextSectionLabel="Why Choose Us?"
            onNextSection={() => whySectionRef?.current?.scrollIntoView({ behavior: 'smooth' })}
            twinkle={phase >= 10}
          />
        </Box>
        <Box mt={10}>
          <Typography variant="body1" sx={{
            opacity: phase >= 11 ? 0.5 : 0,
            transition: 'opacity 0.4s ease',
          }}><i>{isMentourLandingPage ? 'As featured on' : 'Trusted By'}:</i></Typography>
          <Grid container justifyContent="center" alignItems="center" spacing={isMobile ? 2 : 6} direction={isMobile ? 'column' : 'row'}>
            {isMentourLandingPage ? (
              <Grid item>
                <img
                  alt="Mentour"
                  src={mpLogo}
                  style={{
                    height: 44,
                    width: 214,
                    filter: 'grayscale(1) brightness(0%)',
                    opacity: 1,
                    ...isMobile && {
                      marginBottom: 24,
                      marginTop: 40,
                    },
                    ...useDarkTheme && {
                      filter: 'grayscale(1) brightness(10)',
                    }
                  }}
                />
              </Grid>
            ) : (
              <Fragment>
                {[
                  { alt: 'IKEA', src: ikeaLogo, h: 30, w: 150, phase: 11 },
                  { alt: 'Caterpillar', src: catLogo, h: 40, w: 214, phase: 12 },
                  { alt: 'Samsung', src: samsungLogo, h: 100, w: 178, phase: 13 },
                ].map(({ alt, src, h, w, phase: p }) => (
                  <Grid item key={alt}>
                    <img
                      alt={alt}
                      src={src}
                      style={{
                        height: h,
                        width: w,
                        filter: `grayscale(1) brightness(${useDarkTheme ? '10' : '0%'})`,
                        opacity: phase >= p ? 0.7 : 0,
                        transform: phase >= p ? 'translateY(0)' : 'translateY(10px)',
                        transition: 'opacity 0.5s ease, transform 0.5s ease',
                        ...isMobile && alt === 'Caterpillar' && {
                          marginBottom: 24,
                          marginTop: 40,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Fragment>
            )}
          </Grid>
        </Box>
      </Box>

      {/* Skip animation button */}
      <Box sx={{
        position: 'fixed',
        bottom: 30,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 10000,
        opacity: phase > 0 && phase < 11 ? 0.5 : 0,
        pointerEvents: phase > 0 && phase < 11 ? 'auto' : 'none',
        transition: 'opacity 0.4s ease',
      }}>
        <Button
          size="small"
          onClick={skipAnimation}
          sx={{
            textTransform: 'none',
            color: 'rgba(255,255,255,0.5)',
            fontSize: 12,
            '&:hover': { color: 'rgba(255,255,255,0.8)' },
          }}
        >
          Skip Animation
        </Button>
      </Box>
    </BaseSection>
  );
});
